'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Search, Mail, Phone, ChevronLeft, ChevronRight, Loader2, Eye, CheckCircle, Calendar,  } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  topic: string;
  message: string;
  status: string;
  created_at: string;
}

const STATUS_OPTIONS = ['new', 'in_progress', 'resolved', 'closed'];

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB');
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'New', color: 'text-blue-700', bg: 'bg-blue-50' },
  in_progress: { label: 'In Progress', color: 'text-amber-700', bg: 'bg-amber-50' },
  resolved: { label: 'Resolved', color: 'text-green-700', bg: 'bg-green-50' },
  closed: { label: 'Closed', color: 'text-muted-foreground', bg: 'bg-muted' },
};

export default function ContactSubmissionsContent() {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ContactSubmission | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const perPage = 10;

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('contact_submissions')
      .select('*')
      .order('created_at', { ascending: false });
    setSubmissions(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const filtered = submissions.filter((s) => {
    const q = search.toLowerCase();
    const matchSearch =
      s.name.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q) ||
      s.topic.toLowerCase().includes(q) ||
      s.message.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const handleStatusChange = async (id: string, newStatus: string) => {
    setUpdatingStatus(true);
    const supabase = createClient();
    const { error } = await supabase
      .from('contact_submissions')
      .update({ status: newStatus })
      .eq('id', id);
    if (error) {
      toast.error('Failed to update status');
    } else {
      toast.success('Status updated');
      await fetchSubmissions();
      if (selected?.id === id) {
        setSelected((prev) => prev ? { ...prev, status: newStatus } : null);
      }
    }
    setUpdatingStatus(false);
  };

  const openPanel = (s: ContactSubmission) => {
    setSelected(s);
    setPanelOpen(true);
    // Auto-mark as in_progress if new
    if (s.status === 'new') {
      handleStatusChange(s.id, 'in_progress');
    }
  };

  const statusCfg = (status: string) =>
    statusConfig[status] ?? { label: status, color: 'text-muted-foreground', bg: 'bg-muted' };

  return (
    <div className="px-6 py-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-700 text-foreground">Contact Submissions</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage support inquiries and contact form submissions
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total', value: submissions.length },
          { label: 'New', value: submissions.filter((s) => s.status === 'new').length, color: 'text-blue-600' },
          { label: 'In Progress', value: submissions.filter((s) => s.status === 'in_progress').length, color: 'text-amber-600' },
          { label: 'Resolved', value: submissions.filter((s) => s.status === 'resolved').length, color: 'text-green-600' },
        ].map((s) => (
          <div key={s.label} className="card-elevated rounded-xl px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
            <p className={`text-2xl font-700 font-tabular ${s.color ?? 'text-foreground'}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card-elevated rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search submissions..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="all">All Status</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="card-elevated rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Sender</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Topic</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Message</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Received</th>
                <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skel-${i}`}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground text-sm">
                    No submissions found
                  </td>
                </tr>
              ) : (
                paginated.map((s) => {
                  const cfg = statusCfg(s.status);
                  return (
                    <tr
                      key={s.id}
                      className={`hover:bg-muted/30 transition-colors ${s.status === 'new' ? 'bg-blue-50/30' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-600 text-foreground">{s.name}</p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <Mail size={10} />
                          {s.email}
                        </div>
                        {s.phone && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone size={10} />
                            {s.phone}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-accent/10 text-accent font-500">
                          {s.topic || 'General'}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <p className="text-xs text-muted-foreground line-clamp-2">{s.message}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-500 ${cfg.bg} ${cfg.color}`}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar size={10} />
                          {formatDate(s.created_at)}
                        </div>
                        <p className="text-xs text-muted-foreground/60">{timeAgo(s.created_at)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openPanel(s)}
                            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title="View"
                          >
                            <Eye size={14} />
                          </button>
                          {s.status !== 'resolved' && (
                            <button
                              onClick={() => handleStatusChange(s.id, 'resolved')}
                              className="p-1.5 rounded-lg hover:bg-green-50 transition-colors text-muted-foreground hover:text-green-600"
                              title="Mark Resolved"
                            >
                              <CheckCircle size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">
              {filtered.length} submissions · Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Panel Modal */}
      <Modal
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        title="Contact Submission"
        size="md"
      >
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-600 text-muted-foreground mb-1">Name</p>
                <p className="text-sm font-600 text-foreground">{selected.name}</p>
              </div>
              <div>
                <p className="text-xs font-600 text-muted-foreground mb-1">Topic</p>
                <p className="text-sm text-foreground capitalize">{selected.topic || 'General'}</p>
              </div>
              <div>
                <p className="text-xs font-600 text-muted-foreground mb-1">Email</p>
                <a href={`mailto:${selected.email}`} className="text-sm text-accent hover:underline">
                  {selected.email}
                </a>
              </div>
              {selected.phone && (
                <div>
                  <p className="text-xs font-600 text-muted-foreground mb-1">Phone</p>
                  <p className="text-sm text-foreground">{selected.phone}</p>
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-600 text-muted-foreground mb-1">Message</p>
              <div className="bg-muted/40 rounded-lg p-3 text-sm text-foreground leading-relaxed">
                {selected.message}
              </div>
            </div>
            <div>
              <p className="text-xs font-600 text-muted-foreground mb-2">Update Status</p>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => {
                  const cfg = statusCfg(s);
                  return (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(selected.id, s)}
                      disabled={updatingStatus || selected.status === s}
                      className={`text-xs px-3 py-1.5 rounded-full font-500 transition-colors disabled:opacity-60 ${
                        selected.status === s
                          ? `${cfg.bg} ${cfg.color} ring-2 ring-offset-1 ring-current`
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {updatingStatus && selected.status !== s ? (
                        <Loader2 size={10} className="animate-spin inline mr-1" />
                      ) : null}
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Received {formatDate(selected.created_at)} · {timeAgo(selected.created_at)}
              </p>
              <button
                onClick={() => setPanelOpen(false)}
                className="px-4 py-2 text-sm font-500 text-muted-foreground hover:text-foreground transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
